/**
 * Rave Revival — motion
 *
 * Drives the three things CSS cannot do on its own: reveal a group when it
 * crosses into view, pull a control toward a fine pointer, and offset the hero
 * photograph against the scroll.
 *
 * Contract with rr-motion.css:
 *   - `html.rr-motion` is the master switch. It is set by a tiny inline script in
 *     layout/theme.liquid BEFORE first paint, not here: this file is deferred, so
 *     setting it here would let the page paint fully visible and then snap to the
 *     hidden reveal state a moment later. If that class is absent — no script, a
 *     blocked asset, a parse error, reduced motion — every reveal rests in its
 *     visible state and the page reads exactly as it did before any of this.
 *   - `--rr-i` is a child's index in its reveal group; the stagger is a CSS
 *     transition-delay off it.
 *   - `.rr-reveal-in` is added once, when the group first crosses in. Reveals do
 *     not replay on scroll-back; a page that re-animates every time you scroll
 *     past is the thing that makes a site feel like a demo.
 *
 * Nothing here is required for the shop to work. Every entry point checks for its
 * own targets and returns quietly when there are none, so a template without a
 * hero or a room grid costs one querySelectorAll and stops.
 */

(() => {
  'use strict';

  const root = document.documentElement;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  /** Cap the stagger: past this a list reads as a queue, not a rhythm. */
  const MAX_STAGGER_STEPS = 8;
  /** Hero photo travel, in px, across a full viewport of scroll. */
  const PARALLAX_RANGE = 48;
  /** How far a magnetic control is allowed to move from rest, in px. */
  const MAGNET_PULL = 6;
  /** Grace period before concluding the observer is unavailable. See initReveals. */
  const REVEAL_FAILSAFE_MS = 1400;

  const teardowns = [];

  /* ------------------------------------------------------------------ reveals */

  function initReveals() {
    const groups = document.querySelectorAll('[data-rr-reveal]');
    if (!groups.length) return;

    // --rr-i (the child's index, which becomes the stagger) is declared in
    // rr-motion.css with :nth-child rather than written here, so the native
    // scroll-driven path needs no script at all and the two paths cannot drift
    // apart. MAX_STAGGER_STEPS is the cap those rules match.

    if (!('IntersectionObserver' in window)) {
      // No observer: show everything rather than leave it at opacity 0.
      groups.forEach((group) => group.classList.add('rr-reveal-in'));
      return;
    }

    // Proof that the observer is alive. It fires once per observed target on its
    // first pass, whether or not anything is intersecting, so this flips true on a
    // working page even when the whole document starts below the fold.
    let observerAlive = false;

    const observer = new IntersectionObserver(
      (entries) => {
        observerAlive = true;
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('rr-reveal-in');
          observer.unobserve(entry.target); // reveal once, never replay
        });
      },
      {
        // Start the build slightly before the group's top edge arrives, so the
        // first item is already settling as it enters rather than beginning then.
        rootMargin: '0px 0px -12% 0px',
        threshold: 0.01,
      }
    );

    groups.forEach((group) => observer.observe(group));
    teardowns.push(() => observer.disconnect());

    // Failsafe. An IntersectionObserver does not fire at all in a hidden tab, and a
    // viewport with no height — a pane that was never laid out, a print context, an
    // offscreen frame — intersects nothing, so in both cases every group would sit
    // at opacity 0 for as long as that lasted. Caught exactly this way: the reveal
    // groups rendered invisible in a backgrounded preview pane.
    //
    // This deliberately does NOT reveal groups one by one on a timer, which would
    // throw away the scroll reveals on a page that is working perfectly. It asks a
    // narrower question — did the observer ever speak? — and only if the answer is
    // no, after a grace period, does it conclude the mechanism is unavailable and
    // show everything. Worst case is content arriving without its animation. That is
    // the right way round for a shop: never content that does not arrive.
    const failsafe = setTimeout(() => {
      if (observerAlive) return;
      groups.forEach((group) => group.classList.add('rr-reveal-in'));
      observer.disconnect();
    }, REVEAL_FAILSAFE_MS);

    teardowns.push(() => clearTimeout(failsafe));
  }

  /* ---------------------------------------------------------------- magnetic */

  function initMagnetic() {
    if (!finePointer.matches) return;
    const controls = document.querySelectorAll('.rr-magnetic');
    if (!controls.length) return;

    controls.forEach((el) => {
      let frame = 0;

      const onMove = (event) => {
        if (frame) return; // one write per frame, whatever the pointer rate
        frame = requestAnimationFrame(() => {
          frame = 0;
          const box = el.getBoundingClientRect();
          // Offset of the pointer from the control's centre, as -1..1 of its
          // half-size, so a wide button and a small one pull by the same amount.
          const dx = (event.clientX - (box.left + box.width / 2)) / (box.width / 2);
          const dy = (event.clientY - (box.top + box.height / 2)) / (box.height / 2);
          el.style.setProperty('--rr-mx', `${clamp(dx) * MAGNET_PULL}px`);
          el.style.setProperty('--rr-my', `${clamp(dy) * MAGNET_PULL}px`);
        });
      };

      const onEnter = () => el.classList.add('rr-magnetic-active');

      const onLeave = () => {
        if (frame) {
          cancelAnimationFrame(frame);
          frame = 0;
        }
        // Drop the direct-tracking class first so the CSS spring applies to the
        // journey back to rest, then clear the offsets.
        el.classList.remove('rr-magnetic-active');
        el.style.removeProperty('--rr-mx');
        el.style.removeProperty('--rr-my');
      };

      el.addEventListener('pointerenter', onEnter);
      el.addEventListener('pointermove', onMove);
      el.addEventListener('pointerleave', onLeave);
      // A click can move focus and scroll the page out from under the pointer,
      // leaving the control stuck off-centre with no pointerleave coming.
      el.addEventListener('blur', onLeave);

      teardowns.push(() => {
        onLeave();
        el.removeEventListener('pointerenter', onEnter);
        el.removeEventListener('pointermove', onMove);
        el.removeEventListener('pointerleave', onLeave);
        el.removeEventListener('blur', onLeave);
      });
    });
  }

  function clamp(n) {
    return Math.max(-1, Math.min(1, n));
  }

  /* ---------------------------------------------------------------- parallax */

  function initParallax() {
    const hero = document.querySelector('.rr-hero--photo');
    if (!hero) return;

    let frame = 0;

    const update = () => {
      frame = 0;
      const box = hero.getBoundingClientRect();
      // 0 while the hero's top is at or below the viewport top — which is the
      // state at first paint, so the hero as painted is where it has always been.
      // Past that, the photo lags the page by a fraction of the distance scrolled.
      const scrolled = Math.max(0, -box.top);
      const progress = Math.min(1, scrolled / Math.max(1, box.height));
      hero.style.setProperty('--rr-parallax', `${(progress * PARALLAX_RANGE).toFixed(2)}px`);
    };

    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(update);
    };

    // Horizon does not scroll the document on desktop. From 990px up,
    // `.page-wrapper` is `height: 100dvh; overflow-y: auto` (assets/base.css),
    // so the scroll events fire on that element and never reach window — a
    // parallax bound to window alone is simply dead above 990px, which is
    // exactly where a visitor is most likely to see it. Listening to both costs
    // one extra passive listener and covers either scroller; the maths is
    // getBoundingClientRect, which is viewport-relative and correct regardless
    // of which ancestor actually moved.
    const wrapper = document.querySelector('.page-wrapper');

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    if (wrapper) wrapper.addEventListener('scroll', onScroll, { passive: true });

    teardowns.push(() => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (wrapper) wrapper.removeEventListener('scroll', onScroll);
      hero.style.removeProperty('--rr-parallax');
    });
  }

  /* -------------------------------------------------------------------- boot */

  /**
   * Where the browser can run reveals and parallax off a scroll timeline, CSS
   * owns both and this file must not also drive them — two writers on the same
   * property is a fight, not a fallback. The identical test guards the native
   * rules in rr-motion.css section 7. Magnetic pull has no CSS equivalent
   * (there is no timeline for "how near is the pointer"), so it runs either way.
   */
  function nativeScrollDriven() {
    return typeof CSS !== 'undefined'
      && typeof CSS.supports === 'function'
      && CSS.supports('animation-timeline', 'view()');
  }

  function start() {
    if (reduced.matches) return;
    if (!nativeScrollDriven()) {
      initReveals();
      initParallax();
    }
    initMagnetic();
  }

  function stop() {
    while (teardowns.length) {
      try {
        teardowns.pop()();
      } catch (_) {
        /* a torn-down node is not an error worth surfacing */
      }
    }
  }

  // Honour a change of preference in the live page, not just at load: someone
  // turning reduced motion on mid-session should see it stop, not have to reload.
  const onPreferenceChange = () => {
    stop();
    if (reduced.matches) {
      root.classList.remove('rr-motion');
    } else {
      root.classList.add('rr-motion');
      start();
    }
  };

  if (typeof reduced.addEventListener === 'function') {
    reduced.addEventListener('change', onPreferenceChange);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
